import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  Avatar
} from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import LoadingSpinner from '../components/LoadingSpinner';
import axios from 'axios';
import toast from 'react-hot-toast';
import { showToast } from '../utils/toasts';

import Sidebar from '../components/Profile/ProfileSidebar';
import MyAccountTab from '../components/Profile/tabs/MyAccountTab';
import CommunitiesTab from '../components/Profile/tabs/CommunitiesTab';
import PostsTab from '../components/Profile/tabs/PostsTab';
import PrivacyTab from '../components/Profile/tabs/PrivacyTab';
import AuthorizedAppsTab from '../components/Profile/tabs/AuthorizedAppsTab';
import DevicesTab from '../components/Profile/tabs/DevicesTab';
import FamilyCenterTab from '../components/Profile/tabs/FamilyCenterTab';
import AppearanceTab from '../components/Profile/tabs/AppearanceTab';
import AccessibilityTab from '../components/Profile/tabs/AccessibilityTab';
import VoiceVideoTab from '../components/Profile/tabs/VoiceVideoTab';
import NotificationsTab from '../components/Profile/tabs/NotificationsTab';
import KeybindsTab from '../components/Profile/tabs/KeybindsTab';
import ConnectionsTab from '../components/Profile/tabs/ConnectionsTab';
import ImageCropDialog from '../components/Profile/dialogs/ImageCropDialog';
import PasswordDialog from '../components/Profile/dialogs/PasswordDialog';
import UnsavedChangesBar from '../components/Profile/UnsavedChangesBar';
import TextLogo from '../assets/Text-WFHCafe_WObg.png';
import { ProfileSkeleton } from '../components/skeletons';

interface ProfilePageProps {}

const ProfilePage: React.FC<ProfilePageProps> = () => {
  const [activeTab, setActiveTab] = useState('my_account');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEmailRevealed, setIsEmailRevealed] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  
  // Image Cropping State
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [tempImageFile, setTempImageFile] = useState<File | null>(null);
  const [cropType, setCropType] = useState<'profile' | 'banner' | null>(null);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, updateUser, logout, isLoading: authLoading } = useAuth();
  const { mode, toggleTheme } = useTheme();

  const isDark = mode === 'dark';

  // --- LOKI THEME PALETTE ---
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';
  const lightGreen = '#0F3B2E';
  const lokiGradient = 'linear-gradient(135deg, #051814 0%, #0F3B2E 50%, #1D5C4A 100%)';
  
  const colors = {
    sidebarBg: isDark ? 'rgba(5, 24, 20, 0.85)' : 'rgba(255, 255, 255, 0.9)',
    pageBg: isDark ? '#000000' : '#FAFAFA',
    cardBg: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.6)',
    textPrimary: isDark ? '#FFFFFF' : '#051814',
    textSecondary: isDark ? alpha('#FFFFFF', 0.6) : alpha('#051814', 0.6),
    accent: goldColor,
    danger: '#DA373C',
    success: '#23A559',
    iconColor: isDark ? goldColor : lightGreen,
    headerBg: isDark ? 'rgba(0,0,0,0.3)' : '#E3E5E8',
    borderColor: isDark ? alpha(goldColor, 0.2) : alpha(deepGreen, 0.1)
  };

  const discordFont = {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  };

  const glassStyle = {
    backdropFilter: 'blur(20px)',
    border: `1px solid ${colors.borderColor}`,
    boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.05)',
  };

  // Form state
  const [formData, setFormData] = useState<any>({
    username: '',
    bio: '',
    profilePicture: '',
    bannerImage: '',
    socialLinks: {},
    role: '',
  });

  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [bannerImageFile, setBannerImageFile] = useState<File | null>(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        bio: user.bio || '',
        profilePicture: user.profilePicture || '',
        bannerImage: user.bannerImage || '',
        socialLinks: user.socialLinks || {},
        role: user.userRole || '',
      });
    }
  }, [user]);

  useEffect(() => {
    if (tempImageFile) {
      const url = URL.createObjectURL(tempImageFile);
      setTempImageUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [tempImageFile]);

  // --- FIXED LOGOUT LOGIC ---
  const handleLogout = async () => {
    try {
      await axios.post('/api/auth/logout').catch(() => {});
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      await logout(); 
      navigate('/'); 
      toast.success('Logged out successfully');
    }
  };

  const handleSave = async () => {
    if (!user) return;
    setIsSaving(true);
    try {
      const formDataToSend = new FormData();
      if (formData.username) formDataToSend.append('username', formData.username);
      if (formData.bio !== undefined) formDataToSend.append('bio', formData.bio);
      if (formData.role !== undefined) formDataToSend.append('userRole', formData.role);
      if (formData.socialLinks) formDataToSend.append('socialLinks', JSON.stringify(formData.socialLinks));
      if (profileImageFile) formDataToSend.append('profileImage', profileImageFile);
      if (bannerImageFile) formDataToSend.append('bannerImage', bannerImageFile);

      const response = await axios.put('/api/user/profile', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      if (response.data.success) {
        updateUser(response.data.user);
        showToast.profileUpdateSuccess();
        setIsEditing(false);
        setProfileImageFile(null);
        setBannerImageFile(null);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleImageSelect = (file: File, type: 'profile' | 'banner') => {
    setTempImageFile(file);
    setCropType(type);
    setCropModalOpen(true);
  };

  const handleCropSave = () => {
    if (cropType === 'profile') {
      setProfileImageFile(tempImageFile);
    } else if (cropType === 'banner') {
      setBannerImageFile(tempImageFile);
    }
    setCropModalOpen(false);
    setTempImageFile(null);
    setCropType(null);
  };

  const handleCropCancel = () => {
    setCropModalOpen(false);
    setTempImageFile(null);
    setCropType(null);
  };

  const renderContent = () => {
  const commonProps = {
    isDark,
    colors,
    discordFont,
    glassStyle,
    isEditing,
    setIsEditing,
    formData,
    handleFormChange,
    handleImageSelect,
    user,
    profileImageFile,
    bannerImageFile,
    navigate,
    lokiGradient,
    goldColor,
    deepGreen,
    lightGreen,
    setShowPasswordDialog,
    isEmailRevealed,
    mode, // Add this for AppearanceTab
    toggleTheme, // Add this for AppearanceTab
  };

  switch (activeTab) {
    case 'my_account':
      return <MyAccountTab {...commonProps} />;
    case 'profiles':
      return <CommunitiesTab {...commonProps} />;
    case 'posts':
      return <PostsTab {...commonProps} />;
    case 'privacy':  
      return <PrivacyTab {...commonProps} />;
    case 'authorized':
      return <AuthorizedAppsTab {...commonProps} />;
    case 'devices':
      return <DevicesTab {...commonProps} />;
    case 'family':
      return <FamilyCenterTab {...commonProps} />;
    case 'appearance':
      return <AppearanceTab {...commonProps} />;
    case 'accessibility':
      return <AccessibilityTab {...commonProps} />;
    case 'voice':
      return <VoiceVideoTab {...commonProps} />;
    case 'notifications':
      return <NotificationsTab {...commonProps} />;
    case 'keybinds':
      return <KeybindsTab {...commonProps} />;
    case 'connections':
      return <ConnectionsTab {...commonProps} />;
    default:
      return (
        <Box>
          <Typography variant="h5" fontWeight={800} color={colors.textPrimary} sx={{ mb: 1 }}>
            {activeTab.replace('_', ' ')}
          </Typography>
        </Box>
      );
  }
};

  // Both of these previously left the viewport blank or spinning while the
  // auth context resolved; show the profile's own shape instead.
  if (authLoading) return <ProfileSkeleton />;
  if (!user) return <ProfileSkeleton />;

  return (
    <>
      <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 64px)', bgcolor: colors.pageBg, fontFamily: 'Inter, sans-serif' }}>
      
      <Sidebar 
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        handleLogout={handleLogout}
        colors={colors}
        isDark={isDark}
        goldColor={goldColor}
        mobileSidebarOpen={mobileSidebarOpen}
        setMobileSidebarOpen={setMobileSidebarOpen}
      />

      {/* MAIN CONTENT AREA */}
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto', 
        height: 'calc(100vh - 64px)', 
        scrollbarWidth: 'thin',
        width: '100%'
      }}>
        {/* Mobile Header - Add this if you don't have a global header */}
        <Box sx={{ 
          display: { xs: 'flex', md: 'none' }, 
          alignItems: 'center', 
          justifyContent: 'space-between',
          pt: 2,
          px: 2,
          backdropFilter: 'blur(20px)',
          position: 'sticky',
          top: 0,
          zIndex: 1100
        }}>
          <IconButton
            onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
            sx={{ 
              color: colors.textPrimary,
              '&:hover': { bgcolor: alpha(colors.textSecondary, 0.1) }
            }}
          >
            {user?.profilePicture ? (
              <Avatar 
                src={user.profilePicture} 
                sx={{ width: 40, height: 40 }}
              />
            ) : (
              <Avatar sx={{ width: 40, height: 40 }}>
                {user?.username?.charAt(0) || 'U'}
              </Avatar>
            )}
          </IconButton>
          <Box
  onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
  component="img"
  src={TextLogo}
  alt="Profile Settings"
  sx={{
    height: 45,           
    width: 'auto',
  }}
/>
        </Box>
        
        <Container maxWidth="md" sx={{ py: { xs: 3, md: 5, lg: 6 }, px: { xs: 2, sm: 3 } }}>
          {renderContent()}
        </Container>
      </Box>
    </Box>

      <PasswordDialog 
        open={showPasswordDialog}
        onClose={() => setShowPasswordDialog(false)}
        colors={colors}
        onPasswordVerified={() => setIsEmailRevealed(true)}
      />

      <ImageCropDialog
        open={cropModalOpen}
        onClose={handleCropCancel}
        onSave={handleCropSave}
        cropType={cropType}
        tempImageUrl={tempImageUrl}
        colors={colors}
      />

      {isEditing && (
        <UnsavedChangesBar
  isEditing={isEditing}
  setIsEditing={setIsEditing}
  handleSave={handleSave}
  isSaving={isSaving}
  colors={colors}
  isDark={isDark}
  lokiGradient={lokiGradient} 
  goldColor={goldColor} 
/>
      )}
    </>
  );
};

export default ProfilePage;